import { type Location, generatePath } from "react-router";

import { type RecentVisit, type RouteNames } from "src/client";
import {
  RouteGroupMapping,
  RouteNameMapping,
} from "src/experiences/common/constants";

export const localStorageKey = "clusterRecentVisits";

export const isValidRoute = (
  routeName: RouteNames,
  params: Record<string, unknown> | undefined
): boolean => {
  try {
    if (!routeName) return false;

    const pathTemplate = RouteNameMapping[routeName];
    if (!pathTemplate) return false;

    if (params) {
      delete params["*"];
    }

    const resolvedPath = generatePath(pathTemplate, params);
    return !!resolvedPath;
  } catch (error) {
    return false;
  }
};

export const createSortedParamKey = (
  params: Record<string, string | undefined>
) => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      // Include all keys, even if their values are null
      acc[key] = params[key] === null ? "null" : params[key]; // Treat null as a literal 'null' string
      return acc;
    }, {});
  return JSON.stringify(sortedParams);
};

export const recentVisits = (): RecentVisit[] => {
  const visitsString = localStorage.getItem(localStorageKey);
  const visits: RecentVisit[] = visitsString ? JSON.parse(visitsString) : [];
  return visits.filter((visit) => isValidRoute(visit.routeName, visit.params));
};

export const logRecentVisit = (
  routeName: RouteNames,
  params: Record<string, string | undefined> = {},
  location: Location<any>
) => {
  if (!isValidRoute(routeName, params)) {
    return;
  }

  const newVisit: RecentVisit = {
    routeName,
    params,
    timestamp: Date.now(),
  };
  let visits = recentVisits();

  const newPath = generatePath(
    RouteNameMapping[newVisit.routeName],
    newVisit.params
  );

  /*
  we have an undetermined amount of places where our route config, hooks, something, causing multiple mount/unmount
  to render dynamic content within a route element we're trying to log as a recent visit. Deliverables within a entity
  sidenav is a good example. To counter this where we end up logging the wrong recent visit, verify the resolved
  path of the RouteNameMapping matches that of the current browser location to isolate the nav we actually want
  to capture instead of side effect nav happening when these pages render. We're not relying on parsing the path
  for our core logic here so it's not brittle, just using it as a check to verify the event this method receives
  is the final one we actually want to store.
  */
  if (newPath !== location.pathname) {
    return;
  }

  const newParamsKey = createSortedParamKey(params);

  // Filter visits to remove those with the same group and parameters using the group constant and the deterministic param key.
  // ensures we dont blow out recent visit with 20 tiles of the same entity.
  // we cant just use path -
  visits = visits.filter((visit) => {
    const visitGroup = RouteGroupMapping[visit.routeName];
    if (RouteGroupMapping[routeName] === visitGroup) {
      const oldParams = createSortedParamKey(visit.params);
      return newParamsKey !== oldParams;
    }
    return true;
  });

  visits.unshift(newVisit);

  if (visits.length > 50) {
    visits.pop();
  }

  localStorage.setItem(localStorageKey, JSON.stringify(visits));
};
